import Phaser from 'phaser';
import { GAME } from '../config';
import type { Level, Obstacle } from './levels';
import { azimuthToSunVector } from './Sun';

export type Cell = { x: number; y: number };

export type ZoneInfo =
  | { kind: 'roof'; faceIndex: number; pitch: number; multiplier: number; blocked: boolean }
  | {
      kind: 'ground';
      /** 0 (full shade — opposite the sun) → 1 (full sun — toward the sun). */
      exposure: number;
      /** Distance to the closest roof edge, in cells. */
      distanceFromRoof: number;
    }
  | null;

export type FaceInfo = {
  index: number;
  pitch: number; // compass deg
  multiplier: number; // sun-derived 0.3..1
};

/**
 * The roof model.
 *
 * Builds a per-cell map of:
 *   - which face (and its pitch) the cell belongs to,
 *   - whether the cell is blocked by an obstacle,
 *   - which neighbours are also-on-roof (for ridge / eave detection).
 *
 * Owns the rendering of the slate, shingles, ridge lines, eave shadow, and
 * obstacles. Panel sprites are drawn on top by the Scene.
 */
export class Roof {
  scene: Phaser.Scene;
  level: Level;
  origin: { x: number; y: number };
  /** All cells that are part of any roof face. */
  cells: Set<string>;
  /** Cells blocked by obstacles. */
  blocked: Set<string>;
  /** "x,y" → face index. */
  cellFace: Map<string, number>;
  /** Sun multiplier per face index (cached). */
  faceMult: number[];

  graphics!: Phaser.GameObjects.Graphics;
  obstacleGfx!: Phaser.GameObjects.Graphics;
  ridgeGfx!: Phaser.GameObjects.Graphics;
  /** Per-cell shade penalty (kept for compat; always 0 now that scenery is gone). */
  treeShade: Map<string, number> = new Map();
  /** Pixel-space play area where ground placements are allowed. */
  playArea = new Phaser.Geom.Rectangle(0, 0, 0, 0);

  constructor(scene: Phaser.Scene, level: Level, origin: { x: number; y: number }) {
    this.scene = scene;
    this.level = level;
    this.origin = origin;
    this.cells = new Set();
    this.blocked = new Set();
    this.cellFace = new Map();
    this.faceMult = [];

    level.faces.forEach((face, fi) => {
      this.faceMult.push(Roof.computeFaceMult(face.pitch, level.sunAzimuthDeg));
      for (const r of face.cells) {
        for (let dy = 0; dy < r.h; dy++) {
          for (let dx = 0; dx < r.w; dx++) {
            const key = `${r.x + dx},${r.y + dy}`;
            this.cells.add(key);
            // Last face wins on overlap (shouldn't happen in well-formed levels).
            this.cellFace.set(key, fi);
          }
        }
      }
    });
    for (const o of level.obstacles) {
      for (let dy = 0; dy < o.h; dy++) {
        for (let dx = 0; dx < o.w; dx++) {
          this.blocked.add(`${o.x + dx},${o.y + dy}`);
        }
      }
    }
  }

  /** Set the rectangle (in world pixels) inside which ground placement is allowed. */
  setPlayArea(rect: Phaser.Geom.Rectangle) {
    this.playArea = rect;
  }

  /** True if (cx, cy) is part of the roof. */
  isOnRoof(cx: number, cy: number): boolean {
    return this.cells.has(`${cx},${cy}`);
  }

  /**
   * Zone info under a given cell — either a roof face, or a ground spot
   * (with continuous exposure derived from sun direction + position relative
   * to the roof centre). Returns null only when the cell is fully outside
   * the play area.
   */
  zoneAt(cx: number, cy: number): ZoneInfo {
    const key = `${cx},${cy}`;
    if (this.cells.has(key)) {
      const fi = this.cellFace.get(key)!;
      return {
        kind: 'roof',
        faceIndex: fi,
        pitch: this.level.faces[fi].pitch,
        multiplier: this.faceMult[fi],
        blocked: this.blocked.has(key),
      };
    }
    // Ground? Must lie inside the play area pixel rect.
    const cell = GAME.cell;
    const wx = this.origin.x + cx * cell + cell / 2;
    const wy = this.origin.y + cy * cell + cell / 2;
    if (!Phaser.Geom.Rectangle.Contains(this.playArea, wx, wy)) return null;
    return {
      kind: 'ground',
      exposure: this.computeExposure(cx, cy),
      distanceFromRoof: this.distanceToRoofEdge(cx, cy),
    };
  }

  /**
   * 0 = fully shaded (cell sits opposite the sun, behind the house);
   * 1 = fully exposed (cell sits between the sun and the house).
   * Computed as half-space dot of the cell's offset-from-roof-centre with
   * the sun direction vector.
   */
  computeExposure(cellX: number, cellY: number): number {
    const cell = GAME.cell;
    const rb = this.bounds();
    const cx = rb.x + rb.width / 2;
    const cy = rb.y + rb.height / 2;
    const wx = this.origin.x + cellX * cell + cell / 2;
    const wy = this.origin.y + cellY * cell + cell / 2;
    const dx = wx - cx;
    const dy = wy - cy;
    const len = Math.hypot(dx, dy);
    if (len < 0.001) return 0.5;
    const sun = azimuthToSunVector(this.level.sunAzimuthDeg);
    const dot = (dx / len) * sun.x + (dy / len) * sun.y;
    return Math.max(0, Math.min(1, (dot + 1) / 2));
  }

  /** Cell-space distance from cell to nearest roof cell (Chebyshev). 0 if adjacent. */
  distanceToRoofEdge(cellX: number, cellY: number): number {
    let best = Infinity;
    for (const key of this.cells) {
      const [rx, ry] = key.split(',').map(Number);
      const d = Math.max(Math.abs(cellX - rx), Math.abs(cellY - ry));
      if (d < best) best = d;
    }
    return best === Infinity ? 999 : best;
  }

  static computeFaceMult(facePitch: number, sunDeg: number): number {
    const delta = ((facePitch - sunDeg + 540) % 360) - 180;
    const m = Math.cos((delta * Math.PI) / 180);
    return Math.max(GAME.minOrientationMultiplier, m);
  }

  faceForCell(cx: number, cy: number): FaceInfo | null {
    const fi = this.cellFace.get(`${cx},${cy}`);
    if (fi == null) return null;
    return { index: fi, pitch: this.level.faces[fi].pitch, multiplier: this.faceMult[fi] };
  }

  /** Average face multiplier across the cells the panel covers, with tree shade applied. */
  panelMultiplier(cellX: number, cellY: number, w: number, h: number): number {
    let sum = 0;
    let n = 0;
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const key = `${cellX + dx},${cellY + dy}`;
        const fi = this.cellFace.get(key);
        if (fi != null) {
          const faceMult = this.faceMult[fi];
          const shade = this.treeShade.get(key) ?? 0;
          sum += Math.max(GAME.minOrientationMultiplier, faceMult * (1 - shade));
          n++;
        }
      }
    }
    return n === 0 ? 0 : sum / n;
  }

  /** Best-effort cell mult for hover tooltip (combines face + tree shade). */
  effectiveCellMult(cx: number, cy: number): number {
    const fi = this.cellFace.get(`${cx},${cy}`);
    if (fi == null) return 0;
    const shade = this.treeShade.get(`${cx},${cy}`) ?? 0;
    return Math.max(GAME.minOrientationMultiplier, this.faceMult[fi] * (1 - shade));
  }

  draw() {
    if (!this.graphics) {
      this.graphics = this.scene.add.graphics();
      this.obstacleGfx = this.scene.add.graphics();
      this.ridgeGfx = this.scene.add.graphics();
    }
    this.graphics.clear();
    this.obstacleGfx.clear();
    this.ridgeGfx.clear();

    const cell = GAME.cell;
    const bounds = this.bounds();

    // House shadow (offset down-right) — sells the volume
    this.graphics.fillStyle(0x000000, 0.55);
    this.graphics.fillRoundedRect(
      bounds.x + 6,
      bounds.y + 14,
      bounds.width + 4,
      bounds.height + 4,
      10,
    );

    // Eave shadow plinth — slightly larger dark rim "below" the roof
    this.graphics.fillStyle(0x141821, 1);
    this.graphics.fillRoundedRect(
      bounds.x - 6,
      bounds.y - 4,
      bounds.width + 12,
      bounds.height + 12,
      8,
    );

    // Render each cell with face-specific shaded slate + shingles
    for (const key of this.cells) {
      const [cx, cy] = key.split(',').map(Number);
      const fi = this.cellFace.get(key)!;
      const mult = this.faceMult[fi];
      const px = this.origin.x + cx * cell;
      const py = this.origin.y + cy * cell;
      this.drawShingleCell(px, py, cell, mult);
    }

    // Ridge lines (between two adjacent cells on different faces)
    // and eave outlines (cells with no roof neighbour on one side)
    this.drawRidgesAndEaves();

    // Obstacles
    for (const o of this.level.obstacles) {
      this.drawObstacle(o);
    }
  }

  /**
   * Draws one cell as a stack of asphalt-shingle rows whose tone is modulated
   * by the face's sun multiplier.  Brighter = more sun, darker = in shade.
   */
  private drawShingleCell(px: number, py: number, size: number, mult: number) {
    const g = this.graphics;
    // Lerp slate colour by sun mult
    const t = (mult - 0.3) / 0.7; // 0..1
    const baseColor = Roof.lerpColor(0x2c333d, 0x6b7990, t);
    const stripeColor = Roof.lerpColor(0x232a35, 0x556377, t);

    g.fillStyle(baseColor, 1);
    g.fillRect(px, py, size, size);

    // Shingle rows — overlapping horizontal bands with a slight "tab" gap pattern
    const rowH = size / 4;
    for (let r = 0; r < 4; r++) {
      const y = py + r * rowH;
      // Row darker stripe at top edge (suggests the row above's overhang shadow)
      g.fillStyle(0x000000, 0.18);
      g.fillRect(px, y, size, 1.5);
      // Subtle stripe colour fill across most of the row
      g.fillStyle(stripeColor, 0.18);
      g.fillRect(px, y + 1.5, size, rowH * 0.5);

      // Tab slits — small vertical ticks where shingles meet
      // staggered by row so it looks alternating
      const offset = r % 2 === 0 ? 0 : size * 0.5;
      const tabSpacing = size * 0.5;
      g.fillStyle(0x000000, 0.32);
      for (let x = offset; x < size; x += tabSpacing) {
        const tx = px + x;
        if (tx >= px && tx <= px + size) {
          g.fillRect(tx, y + rowH * 0.55, 1, rowH * 0.45);
        }
      }
    }
    // Cell-edge hairline
    g.lineStyle(1, 0x141821, 0.45);
    g.strokeRect(px + 0.5, py + 0.5, size - 1, size - 1);
  }

  private drawRidgesAndEaves() {
    const cell = GAME.cell;
    const g = this.ridgeGfx;
    for (const key of this.cells) {
      const [cx, cy] = key.split(',').map(Number);
      const myFace = this.cellFace.get(key)!;
      const px = this.origin.x + cx * cell;
      const py = this.origin.y + cy * cell;
      // Up
      const upKey = `${cx},${cy - 1}`;
      const upFace = this.cellFace.get(upKey);
      if (upFace == null) {
        // Eave: dark line + thin highlight
        g.lineStyle(2, 0x0a0d12, 1);
        g.lineBetween(px, py, px + cell, py);
        g.lineStyle(1, 0xffffff, 0.08);
        g.lineBetween(px, py + 1, px + cell, py + 1);
      } else if (upFace !== myFace) {
        // Ridge: lighter capping line
        g.lineStyle(2.5, 0xc6cad3, 0.7);
        g.lineBetween(px, py, px + cell, py);
        g.lineStyle(1, 0x000000, 0.25);
        g.lineBetween(px, py + 1.5, px + cell, py + 1.5);
      }
      // Down
      const dnKey = `${cx},${cy + 1}`;
      const dnFace = this.cellFace.get(dnKey);
      if (dnFace == null) {
        g.lineStyle(2, 0x0a0d12, 1);
        g.lineBetween(px, py + cell, px + cell, py + cell);
      } else if (dnFace !== myFace) {
        // Already drawn from below side; skip to avoid double-paint.
      }
      // Left
      const lfKey = `${cx - 1},${cy}`;
      const lfFace = this.cellFace.get(lfKey);
      if (lfFace == null) {
        g.lineStyle(2, 0x0a0d12, 1);
        g.lineBetween(px, py, px, py + cell);
      } else if (lfFace !== myFace) {
        g.lineStyle(2.5, 0xc6cad3, 0.7);
        g.lineBetween(px, py, px, py + cell);
      }
      // Right
      const rtKey = `${cx + 1},${cy}`;
      const rtFace = this.cellFace.get(rtKey);
      if (rtFace == null) {
        g.lineStyle(2, 0x0a0d12, 1);
        g.lineBetween(px + cell, py, px + cell, py + cell);
      }
    }
  }

  private drawObstacle(o: Obstacle) {
    const cell = GAME.cell;
    const px = this.origin.x + o.x * cell;
    const py = this.origin.y + o.y * cell;
    const w = o.w * cell;
    const h = o.h * cell;
    const cx = px + w / 2;
    const cy = py + h / 2;
    // Cast shadow (longer toward sun-opposite direction would be more correct,
    // but a simple drop shadow reads well at this scale)
    this.obstacleGfx.fillStyle(0x000000, 0.5);
    this.obstacleGfx.fillRoundedRect(px + 6, py + 9, w - 8, h - 8, 3);

    if (o.kind === 'chimney') {
      this.obstacleGfx.fillStyle(0x7d3320, 1);
      this.obstacleGfx.fillRoundedRect(px + 4, py + 4, w - 8, h - 8, 3);
      // Brick courses
      this.obstacleGfx.lineStyle(1, 0x4a1a0e, 0.65);
      const courses = 3;
      for (let i = 1; i < courses; i++) {
        const yy = py + 4 + (i * (h - 8)) / courses;
        this.obstacleGfx.lineBetween(px + 4, yy, px + w - 4, yy);
      }
      // Top cap
      this.obstacleGfx.fillStyle(0x2a0f08, 1);
      this.obstacleGfx.fillRect(px + 5, py + 4, w - 10, 5);
      // Flue
      this.obstacleGfx.fillStyle(0x111110, 1);
      this.obstacleGfx.fillCircle(cx, cy, Math.min(w, h) * 0.18);
      // Outline
      this.obstacleGfx.lineStyle(1, 0x4a1a0e, 0.9);
      this.obstacleGfx.strokeRoundedRect(px + 4, py + 4, w - 8, h - 8, 3);
    } else if (o.kind === 'vent') {
      const r = Math.min(w, h) / 2 - 6;
      this.obstacleGfx.fillStyle(0x9aa0a8, 1);
      this.obstacleGfx.fillCircle(cx, cy, r);
      this.obstacleGfx.fillStyle(0x6c7280, 1);
      this.obstacleGfx.fillCircle(cx, cy, r * 0.78);
      this.obstacleGfx.fillStyle(0x2a2e36, 1);
      this.obstacleGfx.fillCircle(cx, cy, r * 0.45);
      this.obstacleGfx.lineStyle(1, 0x9aa0a8, 0.7);
      for (let i = -1; i <= 1; i++) {
        const yy = cy + i * (r * 0.18);
        this.obstacleGfx.lineBetween(cx - r * 0.32, yy, cx + r * 0.32, yy);
      }
      this.obstacleGfx.lineStyle(1, 0xffffff, 0.18);
      this.obstacleGfx.strokeCircle(cx, cy, r);
    } else {
      // Skylight
      const ix = px + 5;
      const iy = py + 5;
      const iw = w - 10;
      const ih = h - 10;
      this.obstacleGfx.fillStyle(0x4a5360, 1);
      this.obstacleGfx.fillRoundedRect(ix, iy, iw, ih, 3);
      this.obstacleGfx.fillStyle(0x6fb5d8, 1);
      this.obstacleGfx.fillRoundedRect(ix + 3, iy + 3, iw - 6, ih - 6, 2);
      this.obstacleGfx.fillStyle(0xc4e6f5, 0.55);
      this.obstacleGfx.fillTriangle(
        ix + 3,
        iy + 3,
        ix + iw / 2,
        iy + 3,
        ix + 3,
        iy + ih / 2,
      );
      this.obstacleGfx.lineStyle(1.5, 0x2a313c, 1);
      this.obstacleGfx.lineBetween(ix + 3, cy, ix + iw - 3, cy);
      this.obstacleGfx.lineBetween(cx, iy + 3, cx, iy + ih - 3);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Snap a world point to the grid cell that contains it. Returns null only
   * if the point is outside the play area entirely.
   */
  worldToCell(worldX: number, worldY: number): Cell | null {
    if (!Phaser.Geom.Rectangle.Contains(this.playArea, worldX, worldY)) return null;
    const cx = Math.floor((worldX - this.origin.x) / GAME.cell);
    const cy = Math.floor((worldY - this.origin.y) / GAME.cell);
    return { x: cx, y: cy };
  }

  cellToWorld(cell: Cell): { x: number; y: number } {
    return {
      x: this.origin.x + cell.x * GAME.cell,
      y: this.origin.y + cell.y * GAME.cell,
    };
  }

  /**
   * Role-aware placement check.
   *   - 'panel'  → all spanned cells must be roof, unblocked, unoccupied.
   *   - 'inverter'/'battery' → all spanned cells must be OFF the roof, inside
   *     the play area, and unoccupied. No visible grid for these — placement
   *     just snaps to the underlying cell grid.
   */
  canPlace(
    role: 'panel' | 'inverter' | 'battery',
    x: number,
    y: number,
    w: number,
    h: number,
    occupied: Set<string>,
  ): boolean {
    const cell = GAME.cell;
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const cx = x + dx;
        const cy = y + dy;
        const key = `${cx},${cy}`;
        if (occupied.has(key)) return false;
        if (role === 'panel') {
          if (!this.cells.has(key)) return false;
          if (this.blocked.has(key)) return false;
        } else {
          if (this.cells.has(key)) return false; // can't sit on the roof
          // Cell centre must lie inside the play area
          const wx = this.origin.x + cx * cell + cell / 2;
          const wy = this.origin.y + cy * cell + cell / 2;
          if (!Phaser.Geom.Rectangle.Contains(this.playArea, wx, wy)) return false;
        }
      }
    }
    return true;
  }

  bounds(): Phaser.Geom.Rectangle {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const key of this.cells) {
      const [cx, cy] = key.split(',').map(Number);
      if (cx < minX) minX = cx;
      if (cy < minY) minY = cy;
      if (cx + 1 > maxX) maxX = cx + 1;
      if (cy + 1 > maxY) maxY = cy + 1;
    }
    return new Phaser.Geom.Rectangle(
      this.origin.x + minX * GAME.cell,
      this.origin.y + minY * GAME.cell,
      (maxX - minX) * GAME.cell,
      (maxY - minY) * GAME.cell,
    );
  }

  static lerpColor(a: number, b: number, t: number): number {
    const tt = Math.max(0, Math.min(1, t));
    const ar = (a >> 16) & 0xff,
      ag = (a >> 8) & 0xff,
      ab = a & 0xff;
    const br = (b >> 16) & 0xff,
      bg = (b >> 8) & 0xff,
      bb = b & 0xff;
    const r = Math.round(ar + (br - ar) * tt);
    const g = Math.round(ag + (bg - ag) * tt);
    const bl = Math.round(ab + (bb - ab) * tt);
    return (r << 16) | (g << 8) | bl;
  }
}
